import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Download } from 'lucide-react'
import { Button } from './primitives'
import { useToast } from './Toast'
import { track } from '@/lib/analytics'
import {
  downloadCsv, downloadJson, downloadMarkdown, toJsonExport, type ExportColumn,
} from '@/lib/export'
import {
  defaultExportScope, exportOptions, exportSets, needsExportChoice, rowsForScope, scopedLabel,
  type ExportScopeId, type ExportSets,
} from '@/lib/exportScope'
import type { CommentParentKind } from '@/lib/comments'
import {
  discussionByItem, discussionColumn, fetchComments, jsonDiscussion, threadsByItem,
  type CommentThread,
} from '@/features/notifications/exportComments'
import { whyNoDiscussion } from '@/features/notifications/discussionError'

/**
 * CSV for a spreadsheet, JSON for something that will read it.
 *
 * They are not the same file with different punctuation. JSON carries the discussion as
 * nested objects with the replies intact, and an envelope saying what the file is, when it
 * was taken and whether it is the whole register — everything a reader needs when the file
 * turns up on its own weeks later. CSV flattens the thread into a cell, because that is
 * all a grid can hold.
 */
export type ExportFormat = 'csv' | 'json'

/**
 * Asking which rows the export means, when there is more than one answer.
 *
 * Pressing Export while a filter is on used to take the filtered rows without a word. That
 * is the right default and the wrong way to arrive at it: the other thing people do is
 * narrow a register to find their bearings and then want the whole file. The rows section
 * only appears when the sets differ — see `needsExportChoice` — so a page showing
 * everything it holds is asked about the format and nothing else.
 */
const FORMATS: Array<{ id: ExportFormat; label: string; hint: string }> = [
  { id: 'csv', label: 'CSV', hint: 'For a spreadsheet. One row each, discussion in a cell.' },
  { id: 'json', label: 'JSON', hint: 'For handing to a tool. Discussion nested, replies intact.' },
]

/** The last format chosen, so somebody who always wants one is not asked twice a day. */
const FORMAT_KEY = 'mc.exportFormat'

const rememberedFormat = (): ExportFormat => {
  try {
    return localStorage.getItem(FORMAT_KEY) === 'json' ? 'json' : 'csv'
  } catch {
    // Private windows and blocked site data throw on access rather than returning null.
    return 'csv'
  }
}

const rememberFormat = (format: ExportFormat) => {
  try { localStorage.setItem(FORMAT_KEY, format) } catch { /* nothing worth reporting */ }
}

function ExportDialog<T>({
  title, sets, busy, formats = true, onChoose, onCancel,
}: {
  title: string
  sets: ExportSets<T>
  /**
   * False for the board document, which is markdown and only markdown. Offering a format
   * that the button cannot produce is worse than offering none.
   */
  formats?: boolean
  /** True while the discussion is being read, which is the one slow part of an export. */
  busy?: boolean
  onChoose: (scope: ExportScopeId, format: ExportFormat) => void
  onCancel: () => void
}) {
  const options = exportOptions(sets)
  const asking = needsExportChoice(sets)
  const [scope, setScope] = useState<ExportScopeId>(() => defaultExportScope(sets))
  const [format, setFormat] = useState<ExportFormat>(rememberedFormat)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel])

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div data-export-backdrop aria-hidden className="fixed inset-0 bg-black/50"
           onClick={onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-scope-title"
        className="relative w-full max-w-md rounded-lg border border-mc-line bg-mc-surface p-5 shadow-xl"
      >
        <h2 id="export-scope-title" className="text-base font-medium text-mc-ink">
          Export {title}
        </h2>
        {asking && (
        <fieldset className="mt-4">
          <legend className="text-xs font-medium tracking-wide text-mc-muted uppercase">
            Which rows
          </legend>
          <div className="mt-2 space-y-1">
            {options.map((option) => (
              <label
                key={option.id}
                /* Sized and spaced like the Checkbox primitive, down to the 44px touch
                   target on a phone — these are the same act to the person reading them. */
                className="flex min-h-[44px] cursor-pointer items-center gap-2.5 text-sm text-mc-body select-none sm:min-h-[32px]"
              >
                <input
                  type="radio"
                  name="export-scope"
                  value={option.id}
                  checked={scope === option.id}
                  onChange={() => setScope(option.id)}
                  className="h-[18px] w-[18px] shrink-0 accent-[#355D7C]"
                />
                <span>
                  {option.label}{' '}
                  <span className="tabular-nums text-mc-muted">
                    ({option.count.toLocaleString()})
                  </span>
                </span>
              </label>
            ))}
          </div>
          {/* Said once, plainly: people do not think of the search box and the funnels as
              different things from the tick-boxes, and the count only adds up if they are
              told that this one word covers all three. */}
          <p className="mt-2 text-xs text-mc-muted">
            Filtered means everything the tick-boxes, the search and the column filters left.
          </p>
        </fieldset>
        )}
        {formats && (
        <fieldset className="mt-4">
          <legend className="text-xs font-medium tracking-wide text-mc-muted uppercase">
            Format
          </legend>
          <div className="mt-2 space-y-1">
            {FORMATS.map((option) => (
              <label
                key={option.id}
                className="flex cursor-pointer items-start gap-2.5 py-1.5 text-sm text-mc-body select-none"
              >
                <input
                  type="radio"
                  name="export-format"
                  value={option.id}
                  checked={format === option.id}
                  onChange={() => setFormat(option.id)}
                  className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-[#355D7C]"
                />
                <span className="min-w-0">
                  {option.label}
                  <span className="block text-xs text-mc-muted">{option.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => { rememberFormat(format); onChoose(scope, format) }}
          >
            {busy ? 'Exporting…' : 'Export'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * Takes a register away as a file.
 *
 * The count on the button is the filtered set — what is on screen, and what the export
 * still defaults to. `all` is the collection behind it, and passing it is what turns the
 * button into a question; a page with nothing to filter passes only `rows` and behaves as
 * it always did. `selected` is for a table that lets rows be ticked.
 */
export function ExportButton<T extends { id: string }>({
  label, rows, all, selected, columns, discussion, disabled,
}: {
  /** What is being exported — becomes part of the filename. */
  label: string
  rows: T[]
  /** Every row the page could show, before any filter. Omit if the page has no filters. */
  all?: T[]
  /** Ticked rows, for a table that offers ticking. */
  selected?: T[]
  columns: ExportColumn<T>[]
  /**
   * The label the comments on this register carry. Given one, the export reads the
   * discussion when it runs — never before, so a page nobody exports from never pays
   * for it — and puts it in whichever shape the chosen format can hold.
   */
  discussion?: CommentParentKind
  disabled?: boolean
}) {
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const sets = exportSets(rows, all, selected)

  const run = async (scope: ExportScopeId, format: ExportFormat) => {
    const chosen = rowsForScope(sets, scope)
    setBusy(true)

    let threads: Map<string, CommentThread[]> | null = null
    if (discussion) {
      try {
        threads = threadsByItem(await fetchComments(discussion))
      } catch (error) {
        // Not fatal: a file without the thread beats no file and an error. But the reason
        // is read off the error rather than guessed — see `whyNoDiscussion`.
        console.error('Could not read the discussion for the export', error)
        toast.warning('Exported without the discussion', whyNoDiscussion(error))
      }
    }

    const name = scopedLabel(label, scope)
    track('export', { page: label, rows: chosen.length, scope, format })
    if (format === 'json') {
      downloadJson(name, toJsonExport(label, scope, chosen, columns, (row) =>
        threads ? { discussion: jsonDiscussion(threads.get(row.id)) } : {}))
    } else {
      const flat = threads ? discussionByItem([...threads.values()].flatMap(
        (list) => list.flatMap((t) => [t, ...t.replies]))) : null
      downloadCsv(name, chosen, flat ? [...columns, discussionColumn<T>(flat)] : columns)
    }

    setBusy(false)
    setAsking(false)
  }

  return (
    <>
      <Button
        onClick={() => setAsking(true)}
        disabled={disabled || sets.all.length === 0}
        title="Download this register — it will ask which rows and which format"
      >
        <span className="flex items-center gap-1.5">
          <Download size={14} aria-hidden />
          {/* An ellipsis because it opens a dialog rather than downloading on the spot,
              and no count because the dialog lists every option with its own — two numbers
              a click apart, meaning different things, is worse than one. */}
          Export…
        </span>
      </Button>
      {asking && (
        <ExportDialog title={label} sets={sets} busy={busy}
                      onChoose={run} onCancel={() => setAsking(false)} />
      )}
    </>
  )
}

/** The same, for a page whose natural export is a document rather than a table. */
export function ExportMarkdownButton<T>({
  label, build, rows, all, selected, children, disabled,
}: {
  label: string
  /**
   * Given the rows that were chosen, so the document matches the answer. May be async:
   * the task board reads its discussion before it writes the document.
   */
  build: (rows: T[]) => string | Promise<string>
  /** Omit for a document that is not a row set — the status snapshot. */
  rows?: T[]
  all?: T[]
  selected?: T[]
  children: React.ReactNode
  disabled?: boolean
}) {
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const sets = exportSets(rows ?? [], all, selected)

  const run = async (scope: ExportScopeId) => {
    const chosen = rowsForScope(sets, scope)
    setBusy(true)
    track('export_markdown', { page: label, rows: chosen.length, scope })
    downloadMarkdown(scopedLabel(label, scope), await build(chosen))
    setBusy(false)
    setAsking(false)
  }

  return (
    <>
      <Button
        onClick={() => (needsExportChoice(sets) ? setAsking(true) : run('all'))}
        disabled={disabled}
        title={`Download ${label} as a markdown file`}
      >
        <span className="flex items-center gap-1.5">
          <Download size={14} aria-hidden />
          {children}
        </span>
      </Button>
      {asking && (
        <ExportDialog title={label} sets={sets} busy={busy} formats={false}
                      onChoose={(scope) => run(scope)} onCancel={() => setAsking(false)} />
      )}
    </>
  )
}
